<template>
  <!-- Ajalugu in the new look, on the client page (Seaded -> Kliendid uues
       kujunduses) and the object page (Objekt uues kujunduses): a row per save
       that changed something, the töökäsk it was on as a chip, and the old
       value struck through beside the new one. The saves that only stamped the
       editor (a töökäsk save does that to its client) wait behind a switch. -->
  <div class="obj-card">
    <div class="cl-tools">
      <div class="obj-filters" role="group" aria-label="Näita">
        <button v-for="option in onlyOptions" :key="option.value" type="button" class="obj-chip"
          :aria-pressed="String(only === option.value)" @click="only = option.value">{{ option.text }}</button>
      </div>
      <label class="cl-quiet">
        <input v-model="showQuiet" type="checkbox">
        Näita salvestusi ilma muudatuseta<template v-if="quiet != null"> ({{ quiet }})</template>
      </label>
    </div>

    <div v-if="loading && !entries.length" class="obj-empty">Laen…</div>
    <div v-else-if="!rows.length" class="obj-empty">Selles valikus pole muudatusi.</div>
    <template v-for="row in rows" :key="row.key">
      <div v-if="row.year" class="cl-year">{{ row.year }}</div>
      <div v-else class="cl-entry" :class="{ 'cl-entry--quiet': row.quiet }">
        <div class="cl-entry__when"><b>{{ row.day }}</b>{{ row.time }}</div>
        <span class="obj-ini" :style="dot(row.color)">{{ row.ini }}</span>
        <div class="cl-entry__main">
          <div class="cl-entry__line">
            <b>{{ row.who }}</b> {{ row.verb }}
            <button v-if="row.event" type="button" class="cl-target" title="Ava töökäsk" @click="viewEvent(row.event)">{{ row.eventLabel }}</button>
            <template v-if="row.after"> {{ row.after }}</template>
          </div>
          <dl v-if="row.changes.length" class="cl-diff">
            <template v-for="(change, i) in row.changes" :key="i">
              <dd v-if="change.text !== undefined" class="cl-diff__text">{{ change.text }}</dd>
              <template v-else>
                <dt>{{ change.label }}</dt>
                <dd><span class="cl-was">{{ change.old }}</span><span class="cl-arrow" aria-label="muutus">→</span><span class="cl-now">{{ change.new }}</span></dd>
              </template>
            </template>
          </dl>
        </div>
      </div>
    </template>

    <div v-if="entries.length" class="cl-more">
      <span>{{ entries.length }} / {{ count }}</span>
      <button v-if="entries.length < count" type="button" class="obj-btn obj-btn--outline obj-btn--sm" :disabled="loading" @click="load(true)">Näita rohkem</button>
    </div>

    <v-dialog v-model="selectedEvent" max-width="700px">
      <event mode="view" :eventId="selectedEventId" @close="selectedEvent = false" />
    </v-dialog>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import { mapActions } from 'vuex'
import helpersMixin from '@/plugins/helpersMixin'
import { initials } from '@/components/frame/frameLinks'
import '@/styles/client-plate.css'

const PAGE = 50
// History.types: what the save did.
const VERBS = { 1: 'muutis', 2: 'kustutas', 3: 'lisas' }

export default {
  mixins: [ helpersMixin ],
  props: {
    // One of the two: whose history this is. `record` is that client or
    // object, for the row that says who added it.
    clientId: {
      default: null
    },
    objectId: {
      default: null
    },
    record: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      entries: [],
      count: 0,
      quiet: null,
      only: '',
      showQuiet: false,
      onlyOptions: [
        { value: '', text: 'Kõik' },
        { value: 'calendar', text: 'Töökäsud' },
        this.objectId ? { value: 'object', text: 'Objekti andmed' } : { value: 'client', text: 'Kliendi andmed' }
      ]
    }
  },
  computed: {
    // 'client' or 'object': what the page's own rows are about, in words.
    own () {
      return this.objectId ? { kind: 'object', of: 'objekti', it: 'objekti' } : { kind: 'client', of: 'kliendi', it: 'kliendi' }
    },
    rows () {
      const rows = []
      let year = null
      const push = (date, row) => {
        const y = dayjs(date).format('YYYY')
        if (y !== year) {
          year = y
          rows.push({ key: 'y' + y + rows.length, year: y })
        }
        rows.push(row)
      }
      for (const entry of this.entries) {
        const isEvent = entry.object === 'calendar'
        const changes = this.merged(entry.changes || [])
        const quiet = !changes.length
        const verb = VERBS[entry.typeInfo && entry.typeInfo.type] || 'muutis'
        const who = (entry.creator && entry.creator.fullName) || 'Tundmatu'
        push(entry.dateCreated, {
          key: entry.id,
          day: dayjs(entry.dateCreated).format('DD.MM.YYYY'),
          time: dayjs(entry.dateCreated).format('HH:mm'),
          who,
          ini: initials(who),
          color: entry.creator && entry.creator.color,
          quiet,
          verb: quiet
            ? 'salvestas ' + (isEvent ? 'töökäsu' : this.own.it) + ' ilma muudatuseta'
            : isEvent ? verb + ' töökäsku' : verb + (verb === 'muutis' ? ' ' + this.own.of + ' andmeid' : ' ' + this.own.it),
          event: isEvent ? { id: entry.objectId } : null,
          eventLabel: isEvent ? this.eventLabel(entry.calendarModel) : '',
          changes
        })
      }
      // The record's own beginning: the history table has no row for it, the
      // client or object has its adder and date. Last, once the list reaches
      // back to it.
      const record = this.record || {}
      if (this.only !== 'calendar' && this.entries.length >= this.count && record.createdDate && dayjs(record.createdDate).isValid()) {
        const who = (record.adder && record.adder.fullName) || 'Tundmatu'
        push(record.createdDate, {
          key: 'created',
          day: dayjs(record.createdDate).format('DD.MM.YYYY'),
          time: dayjs(record.createdDate).format('HH:mm'),
          who,
          ini: initials(who),
          color: record.adder && record.adder.color,
          quiet: false,
          verb: 'lisas ' + this.own.it,
          event: null,
          changes: []
        })
      }
      return rows
    },
    params () {
      return {
        clientId: this.clientId || undefined,
        objectId: this.objectId || undefined,
        changesOnly: this.showQuiet ? undefined : true,
        only: this.only || undefined
      }
    }
  },
  watch: {
    params: {
      handler () {
        this.load(false)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    async load (more) {
      this.loading = true
      const params = this.params
      try {
        const result = await api.getHistory({ ...params, limit: PAGE, offset: more ? this.entries.length : 0 })
        // A filter changed while this page was on its way.
        if (params !== this.params) return
        this.entries = more ? this.entries.concat(result.entries) : result.entries
        this.count = result.count
        if (result.quiet != null) this.quiet = result.quiet
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    eventLabel (event) {
      if (!event || !event.startDate) return 'Töökäsk'
      const category = event.category != null ? this.calendarCategoryById(event.category).text : ''
      return 'Töökäsk ' + dayjs(event.startDate).format('DD.MM.YYYY') + (category && category !== '-' ? ' · ' + category : '')
    },
    // Algus and Lõpp moved together are one move: one Aeg line.
    merged (changes) {
      const start = changes.find(change => change.field === 'startDate')
      const end = changes.find(change => change.field === 'endDate')
      if (!start || !end) return changes
      const span = (from, to) => {
        if (from === '—' || to === '—') return from + ' – ' + to
        return from.slice(0, 10) === to.slice(0, 10) ? from + '–' + to.slice(11) : from + ' – ' + to
      }
      const aeg = { field: 'time', label: 'Aeg', old: span(start.old, end.old), new: span(start.new, end.new) }
      return changes.filter(change => change !== end).map(change => change === start ? aeg : change)
    },
    // A person's colour behind the initials; a pale one gets dark ink.
    dot (color) {
      if (!color) return { background: 'var(--p-ink-3)', color: '#fff' }
      const hex = color.replace('#', '')
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex
      const [r, g, b] = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16))
      const light = (0.299 * r + 0.587 * g + 0.114 * b) > 150
      return { background: color, color: light ? '#16322A' : '#fff' }
    }
  },
  mounted () {
    this.load(false)
  }
}
</script>
